import type { RowHeights } from 'components/LazyRowContainer/RowHeights';
import type { RowWithOffset } from 'components/LazyRowContainer/helpers/RowWithOffset';

const zeroLastRow: RowWithOffset = {
  index: 0,
  offset: 0,
};

/**
 * Determine index of a row containing given offset, i.e. first row to be rendered when scrolled at that offset
 *
 * UNSAFE: this function contains optimization techniques that require some unobvious constraints for passed in
 * parameters described below.
 *
 * The index cannot be trivially determined since we have custom row heights, even considering
 * that number of custom heights remains small. In general, we should continue summing up
 * row sizes from 0 until we reach a number higher than the desired offset.
 *
 * But there is a trick, assuming that the scenario where performance matters most is when user fluently scrolls.
 * In that case we can start at last remembered index/offset and look up from there.
 * However, that means lastRow.offset must be calculated from current default row height and custom row heights.
 * If not, this function will produce wrong result. So, you can use lastRow during scroll, but you should
 * pass in undefined in case defaultRowHeight or customRowHeights change.
 *
 * I believe that the scenario of changing row heights can be optimized by calculating some diff to avoid full
 * recalculation too if needed, but it would not be easy, so I decided to not implement it until it is actually needed.
 */
export const unsafeFindRowIndexAtOffset = (
  defaultRowHeight: number,
  customRowHeights: RowHeights,
  offset: number,
  lastRow: RowWithOffset = zeroLastRow,
): RowWithOffset => {
  // Start at last known index.
  // Invariant: newOffset contains top offset of row at newIndex.
  let { index: newIndex, offset: newOffset } = lastRow;

  if (offset >= lastRow.offset) {
    //------
    // Looked up offset is greater than previously known row starts at
    // (or equal, that case is consumed by this branch for simplicity)
    //------

    // Invariant: nextOffset contains bottom offset of row at newIndex.
    let nextOffset =
      newOffset + (customRowHeights.get(newIndex) ?? defaultRowHeight);

    // Repeat while row at newIndex ends before the looked up offset.
    while (nextOffset <= offset) {
      newIndex += 1;
      newOffset = nextOffset;
      nextOffset += customRowHeights.get(newIndex) ?? defaultRowHeight;
    }

    // Return first row that ends after given scroll offset - that is the row visible at given offset.
    return newIndex === lastRow.index
      ? lastRow
      : {
          index: newIndex,
          offset: newOffset,
        };
  }
  //------
  // Looked up offset is lower than previously known row starts at
  //------

  // Repeat while row at newIndex starts after the looked up offset.
  while (newOffset > offset) {
    newIndex -= 1;
    newOffset -= customRowHeights.get(newIndex) ?? defaultRowHeight;
  }

  // Return first row that starts before or on given scroll offset - that is the row visible at given offset.
  // No check for same as previous index is needed in this branch - when we look up an offset before current row
  // start, the result must always be at least one row lower.
  return {
    index: newIndex,
    offset: newOffset,
  };
};
